/**
 * Componente para gestión de Anexos Contractuales
 * Permite crear y aplicar anexos para modificar PSP/CPS sin perder trazabilidad
 */

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FileText, Calendar, DollarSign, Clock, AlertTriangle, 
  CheckCircle, XCircle, Plus, History, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const AnexosContractuales = ({ psp, isOpen, onClose, onAnexoAplicado }) => {
  const [anexos, setAnexos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('lista');
  const [tipoAnexo, setTipoAnexo] = useState('');
  
  // Estados para formularios de cada tipo de anexo
  const [formTermino, setFormTermino] = useState({
    motivo: '',
    accion_saldo: 'condonar',
    porcentaje_penalidad: 0,
    fecha_termino: new Date().toISOString().split('T')[0]
  });
  
  const [formReprogramacion, setFormReprogramacion] = useState({
    motivo: '',
    nueva_cantidad_cuotas: 1,
    fecha_inicio: new Date().toISOString().split('T')[0]
  });
  
  const [formAjustePrecio, setFormAjustePrecio] = useState({
    motivo: '',
    nuevo_precio_uf: 0,
    aplicar_desde_cuota: 1
  });
  
  const [formExtension, setFormExtension] = useState({
    motivo: '',
    meses_extension: 1,
    agregar_cuotas: false,
    monto_cuotas_adicionales_uf: 0
  });

  useEffect(() => {
    if (isOpen && psp?.id) {
      cargarAnexos();
    }
  }, [isOpen, psp?.id]);

  const cargarAnexos = async () => {
    try {
      const response = await api.get(`/anexos/psp/${psp.id}`);
      setAnexos(response.data);
    } catch (error) {
      console.error('Error cargando anexos:', error);
    }
  };

  const crearAnexoTermino = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        psp_id: psp.id,
        motivo: formTermino.motivo,
        accion_saldo: formTermino.accion_saldo,
        fecha_termino: formTermino.fecha_termino
      });
      
      if (formTermino.accion_saldo === 'penalidad') {
        params.append('porcentaje_penalidad', formTermino.porcentaje_penalidad);
      }
      
      const response = await api.post(`/anexos/termino-anticipado?${params.toString()}`);
      toast.success(`Anexo de término anticipado creado (Nº ${response.data.numero_anexo})`);
      cargarAnexos();
      setActiveTab('lista');
      resetForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear anexo');
    } finally {
      setLoading(false);
    }
  };

  const crearAnexoReprogramacion = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        psp_id: psp.id,
        motivo: formReprogramacion.motivo,
        nueva_cantidad_cuotas: formReprogramacion.nueva_cantidad_cuotas,
        fecha_inicio: formReprogramacion.fecha_inicio
      });
      
      const response = await api.post(`/anexos/reprogramacion-cuotas?${params.toString()}`);
      toast.success(`Anexo de reprogramación creado (Nº ${response.data.numero_anexo})`);
      cargarAnexos();
      setActiveTab('lista');
      resetForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear anexo');
    } finally {
      setLoading(false);
    }
  };

  const crearAnexoAjustePrecio = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        psp_id: psp.id,
        motivo: formAjustePrecio.motivo,
        nuevo_precio_uf: formAjustePrecio.nuevo_precio_uf,
        aplicar_desde_cuota: formAjustePrecio.aplicar_desde_cuota
      });
      
      const response = await api.post(`/anexos/ajuste-precio?${params.toString()}`);
      toast.success(`Anexo de ajuste de precio creado (Nº ${response.data.numero_anexo})`);
      cargarAnexos();
      setActiveTab('lista');
      resetForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear anexo');
    } finally {
      setLoading(false);
    }
  };

  const crearAnexoExtension = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        psp_id: psp.id,
        motivo: formExtension.motivo,
        meses_extension: formExtension.meses_extension,
        agregar_cuotas: formExtension.agregar_cuotas
      });
      
      if (formExtension.agregar_cuotas && formExtension.monto_cuotas_adicionales_uf > 0) {
        params.append('monto_cuotas_adicionales_uf', formExtension.monto_cuotas_adicionales_uf);
      }
      
      const response = await api.post(`/anexos/extension-plazo?${params.toString()}`);
      toast.success(`Anexo de extensión creado (Nº ${response.data.numero_anexo})`);
      cargarAnexos();
      setActiveTab('lista');
      resetForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear anexo');
    } finally {
      setLoading(false);
    }
  };

  const aplicarAnexo = async (anexoId) => {
    if (!confirm('¿Está seguro de aplicar este anexo? Esta acción modificará el contrato.')) {
      return;
    }
    
    setLoading(true);
    try {
      await api.post(`/anexos/${anexoId}/aplicar`);
      toast.success('Anexo aplicado correctamente');
      cargarAnexos();
      if (onAnexoAplicado) {
        onAnexoAplicado();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aplicar anexo');
    } finally {
      setLoading(false);
    }
  };

  const eliminarAnexo = async (anexoId) => {
    if (!confirm('¿Está seguro de eliminar este anexo?')) {
      return;
    }
    
    try {
      await api.delete(`/anexos/${anexoId}`);
      toast.success('Anexo eliminado');
      cargarAnexos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar anexo');
    }
  };

  const resetForms = () => {
    setTipoAnexo('');
    setFormTermino({ motivo: '', accion_saldo: 'condonar', porcentaje_penalidad: 0, fecha_termino: new Date().toISOString().split('T')[0] });
    setFormReprogramacion({ motivo: '', nueva_cantidad_cuotas: 1, fecha_inicio: new Date().toISOString().split('T')[0] });
    setFormAjustePrecio({ motivo: '', nuevo_precio_uf: psp?.precio_uf || 0, aplicar_desde_cuota: 1 });
    setFormExtension({ motivo: '', meses_extension: 1, agregar_cuotas: false, monto_cuotas_adicionales_uf: 0 });
  };

  const getEstadoBadge = (estado) => {
    const colores = {
      'Borrador': 'bg-slate-100 text-slate-700',
      'Pendiente de Aprobación': 'bg-amber-100 text-amber-700',
      'Aprobado': 'bg-blue-100 text-blue-700',
      'Aplicado': 'bg-green-100 text-green-700',
      'Rechazado': 'bg-red-100 text-red-700'
    };
    return colores[estado] || 'bg-slate-100 text-slate-700';
  };

  const getTipoIcon = (tipo) => {
    switch (tipo) {
      case 'Término Anticipado': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'Reprogramación de Cuotas': return <Calendar className="w-4 h-4 text-blue-500" />;
      case 'Ajuste de Precio': return <DollarSign className="w-4 h-4 text-green-500" />;
      case 'Extensión de Plazo': return <Clock className="w-4 h-4 text-purple-500" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  // Calcular info de cuotas para mostrar
  const cuotasPagadas = psp?.cuotas?.filter(c => c.status === 'Pagada')?.length || 0;
  const cuotasPendientes = psp?.cuotas?.filter(c => ['Por Facturar', 'Facturada'].includes(c.status))?.length || 0;
  const montoPendiente = psp?.cuotas?.filter(c => ['Por Facturar', 'Facturada'].includes(c.status))
    .reduce((sum, c) => sum + (c.monto || 0), 0) || 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Anexos Contractuales - {psp?.folio}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="lista">
              <History className="w-4 h-4 mr-2" />
              Historial de Anexos ({anexos.length})
            </TabsTrigger>
            <TabsTrigger value="crear">
              <Plus className="w-4 h-4 mr-2" />
              Crear Nuevo Anexo
            </TabsTrigger>
          </TabsList>

          {/* Tab: Lista de Anexos */}
          <TabsContent value="lista" className="space-y-4">
            {anexos.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No hay anexos para este contrato</p>
              </div>
            ) : (
              <div className="space-y-3">
                {anexos.map((anexo) => (
                  <Card key={anexo.id} className="border">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          {getTipoIcon(anexo.tipo_anexo)}
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">Anexo Nº {anexo.numero_anexo}</span>
                              <Badge className={getEstadoBadge(anexo.estado)}>
                                {anexo.estado}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-600">{anexo.tipo_anexo}</p>
                            <p className="text-xs text-slate-500 mt-1">{anexo.motivo}</p>
                            <p className="text-xs text-slate-400 mt-1">
                              Creado: {new Date(anexo.fecha_creacion).toLocaleDateString('es-CL')} por {anexo.usuario_creador}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {anexo.estado === 'Borrador' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => aplicarAnexo(anexo.id)}
                                disabled={loading}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Aplicar
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => eliminarAnexo(anexo.id)}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* Mostrar impacto financiero */}
                      {anexo.impacto_financiero && (
                        <div className="mt-3 p-2 bg-slate-50 rounded text-xs">
                          <strong>Impacto Financiero:</strong>
                          <div className="grid grid-cols-2 gap-2 mt-1">
                            {Object.entries(anexo.impacto_financiero).map(([key, value]) => (
                              <span key={key}>
                                {key.replace(/_/g, ' ')}: <strong>{typeof value === 'number' ? value.toLocaleString('es-CL') : value}</strong>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Tab: Crear Anexo */}
          <TabsContent value="crear" className="space-y-4">
            {/* Info del contrato actual */}
            <Card className="bg-slate-50">
              <CardContent className="p-4">
                <h4 className="font-medium mb-2">Estado actual del contrato</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Precio:</span>
                    <span className="ml-2 font-medium">{psp?.precio_uf} UF</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Cuotas pagadas:</span>
                    <span className="ml-2 font-medium">{cuotasPagadas} de {psp?.cuotas?.length || 0}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Saldo pendiente:</span>
                    <span className="ml-2 font-medium">${montoPendiente.toLocaleString('es-CL')}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Selector de tipo de anexo */}
            <div>
              <Label>Tipo de Anexo</Label>
              <Select value={tipoAnexo} onValueChange={setTipoAnexo}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccione el tipo de anexo..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="termino">
                    <div className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-500" />
                      Término Anticipado
                    </div>
                  </SelectItem>
                  <SelectItem value="reprogramacion">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-blue-500" />
                      Reprogramación de Cuotas
                    </div>
                  </SelectItem>
                  <SelectItem value="ajuste_precio">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-green-500" />
                      Ajuste de Precio
                    </div>
                  </SelectItem>
                  <SelectItem value="extension">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-purple-500" />
                      Extensión de Plazo
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Formulario: Término Anticipado */}
            {tipoAnexo === 'termino' && (
              <Card className="border-red-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    Término Anticipado del Contrato
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Motivo del término</Label>
                    <Textarea
                      value={formTermino.motivo}
                      onChange={(e) => setFormTermino({...formTermino, motivo: e.target.value})}
                      placeholder="Describa el motivo del término anticipado..."
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Acción sobre el saldo pendiente</Label>
                      <Select 
                        value={formTermino.accion_saldo} 
                        onValueChange={(v) => setFormTermino({...formTermino, accion_saldo: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="condonar">Condonar saldo</SelectItem>
                          <SelectItem value="cobrar">Cobrar saldo completo</SelectItem>
                          <SelectItem value="penalidad">Aplicar penalidad</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>Fecha de término</Label>
                      <Input
                        type="date"
                        value={formTermino.fecha_termino}
                        onChange={(e) => setFormTermino({...formTermino, fecha_termino: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  {formTermino.accion_saldo === 'penalidad' && (
                    <div>
                      <Label>Porcentaje de penalidad (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={formTermino.porcentaje_penalidad}
                        onChange={(e) => setFormTermino({...formTermino, porcentaje_penalidad: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                  )}
                  
                  <Button 
                    className="w-full" 
                    variant="destructive"
                    onClick={crearAnexoTermino}
                    disabled={loading || !formTermino.motivo}
                  >
                    Crear Anexo de Término
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Formulario: Reprogramación de Cuotas */}
            {tipoAnexo === 'reprogramacion' && (
              <Card className="border-blue-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-blue-500" />
                    Reprogramación de Cuotas
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-3 bg-blue-50 rounded text-sm">
                    <strong>Cuotas pendientes:</strong> {cuotasPendientes} cuotas por ${montoPendiente.toLocaleString('es-CL')}
                  </div>
                  
                  <div>
                    <Label>Motivo de la reprogramación</Label>
                    <Textarea
                      value={formReprogramacion.motivo}
                      onChange={(e) => setFormReprogramacion({...formReprogramacion, motivo: e.target.value})}
                      placeholder="Describa el motivo de la reprogramación..."
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Nueva cantidad de cuotas</Label>
                      <Input
                        type="number"
                        min="1"
                        value={formReprogramacion.nueva_cantidad_cuotas}
                        onChange={(e) => setFormReprogramacion({...formReprogramacion, nueva_cantidad_cuotas: parseInt(e.target.value) || 1})}
                      />
                    </div>
                    
                    <div>
                      <Label>Fecha inicio nuevas cuotas</Label>
                      <Input
                        type="date"
                        value={formReprogramacion.fecha_inicio}
                        onChange={(e) => setFormReprogramacion({...formReprogramacion, fecha_inicio: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  <div className="p-3 bg-slate-50 rounded text-sm">
                    <ArrowRight className="w-4 h-4 inline mr-2" />
                    {cuotasPendientes} cuotas actuales se reemplazarán por {formReprogramacion.nueva_cantidad_cuotas} cuotas de ~${Math.round(montoPendiente / formReprogramacion.nueva_cantidad_cuotas).toLocaleString('es-CL')} c/u
                  </div>
                  
                  <Button 
                    className="w-full"
                    onClick={crearAnexoReprogramacion}
                    disabled={loading || !formReprogramacion.motivo}
                  >
                    Crear Anexo de Reprogramación
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Formulario: Ajuste de Precio */}
            {tipoAnexo === 'ajuste_precio' && (
              <Card className="border-green-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-green-500" />
                    Ajuste de Precio
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Motivo del ajuste</Label>
                    <Textarea
                      value={formAjustePrecio.motivo}
                      onChange={(e) => setFormAjustePrecio({...formAjustePrecio, motivo: e.target.value})}
                      placeholder="Describa el motivo del ajuste de precio..."
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Precio actual (UF)</Label>
                      <Input value={psp?.precio_uf || 0} disabled className="bg-slate-100" />
                    </div>
                    
                    <div>
                      <Label>Nuevo precio (UF)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formAjustePrecio.nuevo_precio_uf}
                        onChange={(e) => setFormAjustePrecio({...formAjustePrecio, nuevo_precio_uf: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Aplicar desde cuota Nº</Label>
                    <Input
                      type="number"
                      min="1"
                      max={psp?.cuotas?.length || 1}
                      value={formAjustePrecio.aplicar_desde_cuota}
                      onChange={(e) => setFormAjustePrecio({...formAjustePrecio, aplicar_desde_cuota: parseInt(e.target.value) || 1})}
                    />
                  </div>
                  
                  {formAjustePrecio.nuevo_precio_uf > 0 && (
                    <div className="p-3 bg-slate-50 rounded text-sm">
                      <strong>Diferencia:</strong> {(formAjustePrecio.nuevo_precio_uf - (psp?.precio_uf || 0)).toFixed(2)} UF 
                      ({formAjustePrecio.nuevo_precio_uf > (psp?.precio_uf || 0) ? 'Aumento' : 'Descuento'})
                    </div>
                  )}
                  
                  <Button 
                    className="w-full"
                    onClick={crearAnexoAjustePrecio}
                    disabled={loading || !formAjustePrecio.motivo || formAjustePrecio.nuevo_precio_uf <= 0}
                  >
                    Crear Anexo de Ajuste
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Formulario: Extensión de Plazo */}
            {tipoAnexo === 'extension' && (
              <Card className="border-purple-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="w-4 h-4 text-purple-500" />
                    Extensión de Plazo
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Motivo de la extensión</Label>
                    <Textarea
                      value={formExtension.motivo}
                      onChange={(e) => setFormExtension({...formExtension, motivo: e.target.value})}
                      placeholder="Describa el motivo de la extensión..."
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Plazo actual (meses)</Label>
                      <Input value={psp?.meses_ejecucion || 0} disabled className="bg-slate-100" />
                    </div>
                    
                    <div>
                      <Label>Meses de extensión</Label>
                      <Input
                        type="number"
                        min="1"
                        value={formExtension.meses_extension}
                        onChange={(e) => setFormExtension({...formExtension, meses_extension: parseInt(e.target.value) || 1})}
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="agregar_cuotas"
                      checked={formExtension.agregar_cuotas}
                      onChange={(e) => setFormExtension({...formExtension, agregar_cuotas: e.target.checked})}
                    />
                    <Label htmlFor="agregar_cuotas">Agregar cuotas adicionales</Label>
                  </div>
                  
                  {formExtension.agregar_cuotas && (
                    <div>
                      <Label>Monto total cuotas adicionales (UF)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formExtension.monto_cuotas_adicionales_uf}
                        onChange={(e) => setFormExtension({...formExtension, monto_cuotas_adicionales_uf: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                  )}
                  
                  <div className="p-3 bg-slate-50 rounded text-sm">
                    <strong>Nuevo plazo:</strong> {(psp?.meses_ejecucion || 0) + formExtension.meses_extension} meses
                    {formExtension.agregar_cuotas && formExtension.monto_cuotas_adicionales_uf > 0 && (
                      <span className="ml-2">
                        | {formExtension.meses_extension} cuotas de {(formExtension.monto_cuotas_adicionales_uf / formExtension.meses_extension).toFixed(2)} UF c/u
                      </span>
                    )}
                  </div>
                  
                  <Button 
                    className="w-full"
                    onClick={crearAnexoExtension}
                    disabled={loading || !formExtension.motivo}
                  >
                    Crear Anexo de Extensión
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AnexosContractuales;
